import { useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getDropFileErrorMessage } from '../../utils/get-drop-file-error-message';
import { FileDropZoneInlineView } from './file-drop-zone-inline-view';
import { FileDropZoneStackedView } from './file-drop-zone-stacked-view';

// 10MB is the max file size we can store in the database
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_NUMBER_OF_FILES = 1;
const ACCEPTED_FILE_TYPES = { 'application/pdf': ['.pdf'] };

type Variant = 'STACKED' | 'INLINE';

type FileDropZoneProps = {
  variant: Variant;
  onDropAccepted: DropzoneOptions['onDropAccepted'];
  isLoading: boolean;
};

export const FileDropZone = ({ variant, onDropAccepted, isLoading }: FileDropZoneProps) => {
  const [fileRejection, setFileRejection] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, open, isDragReject } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: MAX_NUMBER_OF_FILES,
    maxSize: MAX_FILE_SIZE,
    // noClick: true is needed to prevent file dialog opening twice
    // See https://react-dropzone.js.org/#using-open-on-click
    noClick: true,
    disabled: isLoading,
    onDropAccepted,
    onDropRejected: (fileRejections) => {
      // More than one rejection could happen, but for simplicity we only show one at a time for now
      const errorMessage = getDropFileErrorMessage(fileRejections[0].errors[0]);

      setFileRejection(errorMessage);
    },
    onDragOver: () => {
      setFileRejection(null);
    },
  });

  const content = getFileDropZoneView({
    variant,
    isLoading,
    fileRejection,
    open,
    setFileRejection,
  });

  const isStacked = variant === 'STACKED';

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex w-full justify-center rounded-lg border border-dashed border-seapoint-gray bg-white p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seapoint-yellow focus-visible:ring-offset-2',
        {
          'py-20': isStacked,
          /* -ml-[calc(50vw-50%)] centers element by making it full width and offsetting left margin
          Example: If viewport is 1200px wide, margin-left will be -600px + 50% of element width,
          pulling element leftward to align with viewport edges */
          '-ml-[calc(50vw-50%)] w-screen border-seapoint-yellow bg-seapoint-yellow/10 py-14': isDragActive,
          'py-14': isDragActive && !isStacked,
          'py-40': isDragActive && isStacked,
          'border-seapoint-red bg-seapoint-red/10': isDragReject,
          'cursor-not-allowed bg-seapoint-gray-background-modal': isLoading,
        },
      )}
    >
      <label aria-label="Upload an invoice to pay" role="presentation">
        <input {...getInputProps()} />
      </label>

      {content}
    </div>
  );
};

export type ViewProps = {
  isLoading: boolean;
  fileRejection: string | null;
  open: ReturnType<typeof useDropzone>['open'];
  setFileRejection: (fileRejection: string | null) => void;
};

type GetFileDropZoneViewProps = {
  variant: Variant;
} & ViewProps;

const getFileDropZoneView = ({ variant, ...props }: GetFileDropZoneViewProps) => {
  switch (variant) {
    case 'INLINE':
      return <FileDropZoneInlineView {...props} />;
    case 'STACKED':
      return <FileDropZoneStackedView {...props} />;
    default:
      return exhaustiveCheck(variant);
  }
};
